import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { submitGradeBlock, GRADES } from '../data';

const SEASONS = ['2025-26', '2024-25', '2023-24', '2022-23'];
const GST_RATE = 0.05;

const STATUS_STYLES = {
  Pending: { bg: '#fef3c7', color: '#92400e' },
  Live: { bg: '#dcfce7', color: '#16a34a' },
  Rejected: { bg: '#fee2e2', color: '#DC143C' },
};

const emptyGradeBlock = () => ({
  id: Date.now() + Math.random(),
  grade: 'S1-30',
  paymentDate: '',
  liftingDate: '',
  rateExclGST: '',
  availableQty: '',
});

const RateEntryPanel = ({ millId, millName, autoOpen }) => {
  const [open, setOpen] = useState(false);

  // Deep-link target from My Submissions' Expired tab ("Update & Resubmit") —
  // force this panel open so the FRO lands directly in the form, not a collapsed
  // card they'd have to click again themselves.
  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);
  const [season, setSeason] = useState('2024-25');
  const [gradeBlocks, setGradeBlocks] = useState([emptyGradeBlock()]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [blockErrors, setBlockErrors] = useState({}); // blockId -> validation message
  const [blockDateErrors, setBlockDateErrors] = useState({}); // blockId -> date validation message
  const [toast, setToast] = useState(null);
  // mill_price_quotes has no rows yet, and this page's given query doesn't read prior submissions for
  // display — this caption reflects only what's happened THIS session, not lifetime history.
  const [lastSubmission, setLastSubmission] = useState(null);

  const updateBlock = (id, field, value) => {
    setGradeBlocks(prev => prev?.map(b => b?.id === id ? { ...b, [field]: value } : b));
    if (field === 'availableQty' && blockErrors?.[id]) {
      setBlockErrors(prev => { const next = { ...prev }; delete next?.[id]; return next; });
    }
    if ((field === 'paymentDate' || field === 'liftingDate') && blockDateErrors?.[id]) {
      setBlockDateErrors(prev => { const next = { ...prev }; delete next?.[id]; return next; });
    }
  };

  const addBlock = () => setGradeBlocks(prev => [...prev, emptyGradeBlock()]);

  const removeBlock = (id) => {
    if (gradeBlocks?.length === 1) return;
    setGradeBlocks(prev => prev?.filter(b => b?.id !== id));
  };

  const calcInclGST = (excl) => {
    const n = parseFloat(excl);
    if (isNaN(n) || excl === '') return '';
    return (n * (1 + GST_RATE))?.toFixed(2);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  // Submit for Approval — INSERTs one mill_price_quotes row per grade block (status='pending_approval').
  // Requires the signed-in user to hold factory_relations_officer; RLS (mpq_officer_insert_own) enforces
  // submitted_by = auth.uid(), not re-checked client-side beyond surfacing the real error on failure.
  const handleSubmit = async () => {
    if (!millId) {
      setSubmitError('No mill_id on this card — cannot submit.');
      return;
    }

    // Required field — a grade block with an empty or zero/negative quantity must not submit.
    const nextBlockErrors = {};
    gradeBlocks?.forEach((block) => {
      const qty = Number(block?.availableQty);
      if (block?.availableQty === '' || block?.availableQty == null || isNaN(qty) || qty <= 0) {
        nextBlockErrors[block?.id] = 'Available Qty (MT) is required and must be greater than 0.';
      }
    });
    // Payment Date cannot be in the past; Lifting Date cannot be before Payment Date.
    const todayStr = new Date()?.toISOString()?.slice(0, 10);
    const nextDateErrors = {};
    gradeBlocks?.forEach((block) => {
      if (block?.paymentDate && block?.paymentDate < todayStr) {
        nextDateErrors[block?.id] = 'Payment Date cannot be in the past.';
      } else if (block?.paymentDate && block?.liftingDate && block?.liftingDate < block?.paymentDate) {
        nextDateErrors[block?.id] = 'Lifting Date cannot be before Payment Date.';
      }
    });

    if (Object.keys(nextBlockErrors)?.length > 0 || Object.keys(nextDateErrors)?.length > 0) {
      setBlockErrors(nextBlockErrors);
      setBlockDateErrors(nextDateErrors);
      setSubmitError('Fix the highlighted grade block(s) before submitting.');
      return;
    }
    setBlockErrors({});
    setBlockDateErrors({});
    setSubmitting(true);
    setSubmitError(null);

    const results = await Promise.all(
      gradeBlocks?.map((block) => submitGradeBlock({
        millId,
        season,
        grade: block?.grade,
        paymentDate: block?.paymentDate || null,
        liftingDate: block?.liftingDate || null,
        rateExclGst: block?.rateExclGST,
        availableQty: block?.availableQty,
      }))
    );

    setSubmitting(false);
    const failures = results?.filter((r) => r?.error);

    if (failures?.length > 0) {
      const message = failures?.[0]?.error;
      setSubmitError(message);
      showToast(`❌ Submit failed: ${message}`);
      return; // do not fake success, do not clear the form
    }

    const { data: { user } } = await supabase?.auth?.getUser();
    const now = new Date();
    setLastSubmission({
      by: user?.email || 'you',
      time: now?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
      date: now?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      status: 'Pending',
    });
    showToast(`✅ Submitted ${gradeBlocks?.length} grade${gradeBlocks?.length !== 1 ? 's' : ''} for approval`);
    setGradeBlocks([emptyGradeBlock()]);
    setBlockErrors({});
    setBlockDateErrors({});
    setOpen(false); // auto-collapse after a successful submit
  };

  const statusStyle = STATUS_STYLES?.[lastSubmission?.status] || STATUS_STYLES?.Pending;

  return (
    <div className="mt-3 rounded-xl border overflow-hidden" style={{ borderColor: '#e8ddd0' }}>
      {/* Toggle header */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 transition-colors hover:bg-amber-50/50"
        style={{ background: open ? '#fffbeb' : '#fafaf9' }}
      >
        <div className="flex items-center gap-2">
          <Icon name="ClipboardEdit" size={15} color="#f59e0b" />
          <span className="text-sm font-bold" style={{ color: '#1f2937' }}>Update Rate</span>
        </div>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} color="#6b7280" />
      </button>

      {/* Last submitted caption — always visible (even collapsed), so it survives auto-collapse after a
          successful submit. Reflects this session only; mill_price_quotes has no history to read yet. */}
      {lastSubmission && (
        <div className="px-4 py-2.5 flex items-center gap-2 flex-wrap" style={{ background: '#fafaf9', borderTop: '1px solid #e8ddd0' }}>
          <p className="text-xs" style={{ color: '#6b7280' }}>
            Last submitted by <span className="font-semibold" style={{ color: '#1f2937' }}>{lastSubmission?.by}</span> on {lastSubmission?.time}, {lastSubmission?.date}
          </p>
          <span
            className="px-2 py-0.5 rounded-full text-xs font-bold"
            style={{ background: statusStyle?.bg, color: statusStyle?.color }}
          >
            {lastSubmission?.status}
          </span>
        </div>
      )}

      {open && (
        <div className="px-4 pb-5 pt-3" style={{ background: '#fafaf9' }}>
          {/* Season dropdown */}
          <div className="mb-4">
            <label className="block text-xs font-semibold mb-1" style={{ color: '#6b7280' }}>Season</label>
            <select
              value={season}
              onChange={e => setSeason(e?.target?.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
              style={{ borderColor: '#e8ddd0', background: '#fff', color: '#1f2937' }}
            >
              {SEASONS?.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>

          {/* Grade blocks */}
          <div className="space-y-0">
            {gradeBlocks?.map((block, idx) => (
              <div key={block?.id}>
                {idx > 0 && <hr className="my-3" style={{ borderColor: '#e8ddd0' }} />}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#92400e' }}>
                      Grade Block {idx + 1}
                    </p>
                    {gradeBlocks?.length > 1 && (
                      <button onClick={() => removeBlock(block?.id)} className="p-1 rounded hover:bg-red-50">
                        <Icon name="Trash2" size={13} color="#DC143C" />
                      </button>
                    )}
                  </div>

                  {/* Grade */}
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#6b7280' }}>Grade</label>
                    <select
                      value={block?.grade}
                      onChange={e => updateBlock(block?.id, 'grade', e?.target?.value)}
                      className="w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
                      style={{ borderColor: '#e8ddd0', background: '#fff', color: '#1f2937' }}
                    >
                      {GRADES?.map(g => <option key={g}>{g}</option>)}
                    </select>
                  </div>

                  {/* Dates row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#6b7280' }}>Payment Date</label>
                      <input
                        type="date"
                        value={block?.paymentDate}
                        onChange={e => updateBlock(block?.id, 'paymentDate', e?.target?.value)}
                        className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        style={{ borderColor: '#e8ddd0', background: '#fff', color: '#1f2937' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#6b7280' }}>Lifting Date</label>
                      <input
                        type="date"
                        value={block?.liftingDate}
                        onChange={e => updateBlock(block?.id, 'liftingDate', e?.target?.value)}
                        className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        style={{ borderColor: blockDateErrors?.[block?.id] ? '#DC143C' : '#e8ddd0', background: '#fff', color: '#1f2937' }}
                      />
                    </div>
                  </div>
                  {blockDateErrors?.[block?.id] && (
                    <p className="text-xs -mt-2" style={{ color: '#DC143C' }}>{blockDateErrors?.[block?.id]}</p>
                  )}

                  {/* Rate fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#6b7280' }}>Rate (Excl. GST) ₹/MT</label>
                      <input
                        type="number"
                        placeholder="e.g. 38000"
                        value={block?.rateExclGST}
                        onChange={e => updateBlock(block?.id, 'rateExclGST', e?.target?.value)}
                        className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        style={{ borderColor: '#e8ddd0', background: '#fff', color: '#1f2937' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1" style={{ color: '#6b7280' }}>Rate (Incl. 5% GST) ₹/MT</label>
                      <input
                        type="text"
                        readOnly
                        value={calcInclGST(block?.rateExclGST)}
                        placeholder="Auto-calculated"
                        className="w-full rounded-lg border px-3 py-2 text-sm cursor-not-allowed"
                        style={{ borderColor: '#e8ddd0', background: '#f3f4f6', color: '#6b7280' }}
                      />
                    </div>
                  </div>

                  {/* Available Qty */}
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#6b7280' }}>Available Qty (MT)</label>
                    <input
                      type="number"
                      placeholder="e.g. 500"
                      min="0"
                      value={block?.availableQty}
                      onChange={e => updateBlock(block?.id, 'availableQty', e?.target?.value)}
                      className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      style={{ borderColor: blockErrors?.[block?.id] ? '#DC143C' : '#e8ddd0', background: '#fff', color: '#1f2937' }}
                    />
                    {blockErrors?.[block?.id] && (
                      <p className="text-xs mt-1" style={{ color: '#DC143C' }}>{blockErrors?.[block?.id]}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add Grade button */}
          <button
            onClick={addBlock}
            className="mt-4 flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg border transition hover:bg-amber-50"
            style={{ borderColor: '#f59e0b', color: '#d97706' }}
          >
            <Icon name="Plus" size={14} color="#d97706" />
            Add Grade
          </button>

          {/* Submit error — real Postgres/RLS error, never a faked success */}
          {submitError && (
            <div
              className="mt-4 flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs"
              style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b' }}
            >
              <Icon name="AlertCircle" size={13} color="#DC143C" className="flex-shrink-0 mt-0.5" />
              {submitError}
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="mt-4 w-full py-3 rounded-xl font-bold text-white text-sm transition hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #f59e0b, #ff8c00)', boxShadow: '0 2px 8px rgba(245,158,11,0.4)' }}
          >
            {submitting ? 'Submitting…' : 'Submit for Approval'}
          </button>

          {/* Toast */}
          {toast && (
            <div
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl text-sm font-semibold text-white"
              style={{ backgroundColor: '#1f2937', minWidth: 280 }}
            >
              <span className="flex-1">{toast}</span>
              <button onClick={() => setToast(null)} className="opacity-70 hover:opacity-100">
                <Icon name="X" size={14} color="#fff" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RateEntryPanel;
